import React, { useLayoutEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "../../lib/motion";

/**
 * Brings content in as it reaches the viewport.
 *
 * Renders no wrapper beyond the element you ask for. `stagger` animates the
 * element's own children instead of the block, which is what card grids and
 * lists want. Reduced motion skips straight to the resting state.
 */
const Reveal = ({
  as: Tag = "div",
  delay = 0,
  distance = 26,
  stagger = 0,
  once = true,
  className = "",
  children,
  ...rest
}) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const targets = stagger ? Array.from(node.children) : node;

    if (prefersReducedMotion()) {
      gsap.set(targets, { clearProps: "all", opacity: 1, y: 0 });
      node.classList.add("is-revealed");
      return undefined;
    }

    let started = false;

    const ctx = gsap.context(() => {
      gsap.set(targets, { opacity: 0, y: distance });
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: 0.75,
        ease: "power3.out",
        delay: delay / 1000,
        stagger: stagger / 1000,
        scrollTrigger: {
          trigger: node,
          start: "top 88%",
          once,
        },
        onStart: () => {
          started = true;
          node.classList.add("is-revealed");
        },
      });
    }, node);

    /* Insurance. If the trigger mis-measures — a late webfont, a resized
       viewport, a headless renderer — anything already on screen must not be
       left invisible. Content outweighs the animation. */
    const failsafe = window.setTimeout(() => {
      if (started) return;
      const { top } = node.getBoundingClientRect();
      if (top < window.innerHeight * 0.98) {
        gsap.set(targets, { opacity: 1, y: 0 });
        node.classList.add("is-revealed");
      }
    }, 1400);

    return () => {
      window.clearTimeout(failsafe);
      ctx.revert();
    };
  }, [delay, distance, stagger, once]);

  return (
    <Tag ref={ref} className={`reveal${className ? ` ${className}` : ""}`} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
